import request from '@/utils/request'

/**
 * 将嵌套对象扁平化为单层对象
 * @param {Object} data - 嵌套的表单数据
 * @returns {Object} - 扁平化的数据
 */
function flattenFormData(data) {
  return {
    // 贵公司基本情况
    legalName: data.legalName,
    legalRepresentativeName: data.legalRepresentative?.name || '',
    legalRepresentativePhone: data.legalRepresentative?.phone || '',
    legalRepresentativeEmail: data.legalRepresentative?.email || '',
    accountingCityProvince: data.accountingCity?.province || '',
    accountingCityCity: data.accountingCity?.city || '',
    accountingCityAddress: data.accountingCity?.address || '',
    registeredCapital: data.registeredCapital,
    establishmentDate: data.establishmentDate,
    creditCode: data.creditCode,
    taxpayerCategory: data.taxpayerCategory,
    officeLocationProvince: data.officeLocation?.province || '',
    officeLocationCity: data.officeLocation?.city || '',
    mainPhone: data.mainPhone,
    
    // 贵公司基本联络信息
    productManagerName: data.productManager?.name || '',
    productManagerPhone: data.productManager?.phone || '',
    productManagerEmail: data.productManager?.email || '',
    backupContactName: data.backupContact?.name || '',
    backupContactPhone: data.backupContact?.phone || '',
    backupContactEmail: data.backupContact?.email || '',
    
    // 甲信拓展人
    expansionManagerName: data.expansionManager?.name || '',
    expansionManagerPhone: data.expansionManager?.phone || '',
    expansionManagerEmail: data.expansionManager?.email || '',
    
    // 申请代理商认证级别
    agentLevel: data.agentLevel
  }
}

/**
 * 提交认证申请
 * @param {Object} data - 认证申请数据（嵌套结构）
 * @returns {Promise}
 */
export function submitCertification(data) {
  // 将嵌套对象转换为扁平化结构
  const flatData = flattenFormData(data)
  
  return request({
    url: '/certification/submit',
    method: 'post',
    data: flatData
  })
}

/**
 * 查询认证申请状态
 * @param {string} uuid - 认证申请UUID
 * @returns {Promise}
 */
export function getCertificationStatus(uuid) {
  return request({
    url: '/certification/status',
    method: 'get',
    params: { uuid }
  })
}

/**
 * 获取认证申请详情
 * @param {string} uuid - 认证申请UUID
 * @returns {Promise}
 */
export function getCertificationDetail(uuid) {
  return request({
    url: `/certification/${uuid}`,
    method: 'get'
  })
}

/**
 * 更新认证申请
 * @param {string} uuid - 认证申请UUID
 * @param {Object} data - 更新的数据（嵌套结构）
 * @returns {Promise}
 */
export function updateCertification(uuid, data) {
  // 将嵌套对象转换为扁平化结构
  const flatData = flattenFormData(data)
  
  return request({
    url: `/certification/${uuid}`,
    method: 'put',
    data: flatData
  })
}

/**
 * 获取用户的所有认证申请列表
 * @param {Object} params - 查询参数
 * @returns {Promise}
 */
export function getCertificationList(params) {
  return request({
    url: '/certification/list',
    method: 'get',
    params
  })
}

/**
 * 撤销认证申请
 * @param {string} uuid - 认证申请UUID
 * @returns {Promise}
 */
export function cancelCertification(uuid) {
  return request({
    url: `/certification/${uuid}/cancel`,
    method: 'post'
  })
}

/**
 * 代理商登录
 * @param {string} certificationId - 认证申请ID（账号）
 * @param {string} password - 密码
 * @returns {Promise}
 */
export function login(certificationId, password) {
  return request({
    url: '/certification/login',
    method: 'post',
    data: {
      certificationId,
      password
    }
  })
}

/**
 * 代理商退出登录
 * @returns {Promise}
 */
export function logout() {
  return request({
    url: '/certification/logout',
    method: 'post'
  })
}

/**
 * 获取当前登录用户信息
 * @returns {Promise}
 */
export function getUserInfo() {
  return request({
    url: '/certification/getUserInfo',
    method: 'get'
  })
}

/**
 * 检查登录状态
 * @returns {Promise}
 */
export function checkLogin() {
  return request({
    url: '/certification/checkLogin',
    method: 'get'
  })
}

/**
 * 获取当前登录账号代理商等级配置
 * 返回结构同 /partner/config/list
 */
export function getPartnerConfigs() {
  return request({
    url: '/certification/configs',
    method: 'get'
  })
}

export function getOrderList(params) {
  return request({
    url: '/certification/myOrders',
    method: 'get',
    params
  })
}

/**
 * 查询产品列表
 */
export function getProductList(params) {
  return request({
    url: '/certification/products',
    method: 'get',
    params
  })
}

/**
 * 获取字典数据
 * @param {string} dictType - 字典类型
 * @returns {Promise}
 */
export function getDictData(dictType) {
  return request({
    url: `/certification/type/${dictType}`,
    method: 'get'
  })
}

/**
 * 上传文件
 * @param {File} file - 文件对象
 * @returns {Promise}
 */
export function uploadFile(file) {
  const formData = new FormData()
  formData.append('file', file)
  
  return request({
    url: '/certification/upload',
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

/**
 * 保存附件信息
 * @param {Object} attachmentData - 附件数据
 * @returns {Promise}
 */
export function saveAttachment(attachmentData) {
  return request({
    url: '/certification/attachment',
    method: 'post',
    data: attachmentData
  })
}

/**
 * 查询附件列表
 * @param {string} projectReportId - 项目报备ID（可为空，查询当前用户的附件）
 * @returns {Promise}
 */
export function getAttachmentList(projectReportId) {
  return request({
    url: '/certification/attachments',
    method: 'get',
    params: { projectReportId }
  })
}

/**
 * 删除附件
 * @param {string} attachmentId - 附件ID
 * @returns {Promise}
 */
export function deleteAttachment(attachmentId) {
  return request({
    url: `/certification/attachment/${attachmentId}`,
    method: 'delete'
  })
}

/**
 * 获取订单统计信息
 * @param {number} year - 年份，可选，默认为当前年份
 * @returns {Promise}
 */
export function getOrderStats(year) {
  const params = {}
  if (year) {
    params.year = year
  }
  return request({
    url: '/certification/orderStats',
    method: 'get',
    params
  })
}

/**
 * 激励计算（初始化）
 * 后端：GET /incentive/calc
 * @param {number} year - 年份，可选，默认为当前年份
 */
export function getIncentiveCalc(year) {
  const params = {}
  if (year) {
    params.year = year
  }
  return request({
    url: '/certification/incentive/calc',
    method: 'get',
    params
  })
}

/**
 * 忘记密码 - 验证邮箱
 * @param {string} certificationId - 认证申请ID
 * @param {string} contactEmail - 联系邮箱
 * @returns {Promise}
 */
export function forgotPassword(certificationId, contactEmail) {
  return request({
    url: '/certification/forgot-password',
    method: 'post',
    data: {
      certificationId,
      contactEmail
    }
  })
}

/**
 * 重置密码
 * @param {string} certificationId - 认证申请ID
 * @param {string} contactEmail - 联系邮箱
 * @param {string} newPassword - 新密码
 * @returns {Promise}
 */
export function resetPassword(certificationId, contactEmail, newPassword) {
  return request({
    url: '/certification/reset-password',
    method: 'post',
    data: {
      certificationId,
      contactEmail,
      newPassword
    }
  })
}

/**
 * 修改密码（需要登录）
 * @param {string} oldPassword - 原密码
 * @param {string} newPassword - 新密码
 * @param {string} confirmPassword - 确认密码
 * @returns {Promise}
 */
export function changePassword(oldPassword, newPassword, confirmPassword) {
  return request({
    url: '/certification/changePassword',
    method: 'post',
    data: {
      oldPassword,
      newPassword,
      confirmPassword
    }
  })
}

/**
 * 保存项目报备（创建或更新）
 * @param {Object} reportData - 项目报备数据
 * @returns {Promise}
 */
export function saveProjectReport(reportData) {
  return request({
    url: '/certification/project-report/save',
    method: 'post',
    data: reportData
  })
}

/**
 * 提交项目报备
 * @param {string} reportNo - 项目报备编号
 * @returns {Promise}
 */
export function submitProjectReport(reportNo) {
  return request({
    url: '/certification/project-report/submit',
    method: 'post',
    data: { reportNo }
  })
}

/**
 * 撤回项目报备
 * @param {string} reportNo - 项目报备编号
 * @returns {Promise}
 */
export function withdrawProjectReport(reportNo) {
  return request({
    url: '/certification/project-report/withdraw',
    method: 'post',
    data: { reportNo }
  })
}

/**
 * 查询项目报备列表
 * @param {Object} params - 查询参数 {status, flowStatus}
 * @returns {Promise}
 */
export function getProjectReportList(params) {
  return request({
    url: '/certification/project-reports',
    method: 'get',
    params
  })
}

/**
 * 查询项目报备详情
 * @param {string} reportNo - 项目报备编号
 * @returns {Promise}
 */
export function getProjectReportDetail(reportNo) {
  return request({
    url: `/certification/project-report/${reportNo}`,
    method: 'get'
  })
}

/**
 * 提交渠道优秀代表申请
 * @param {Object} data - 申请数据 { year, representatives: [{ role, name, bankAccount, openingBank }] }
 * @returns {Promise}
 */
export function submitExcellentRepresentativeApplication(data) {
  return request({
    url: '/certification/excellent-representative/apply',
    method: 'post',
    data
  })
}

/**
 * 获取渠道优秀代表申请列表（当前用户）
 * @returns {Promise}
 */
export function getExcellentRepresentativeApplications() {
  return request({
    url: '/certification/excellent-representative/list',
    method: 'get'
  })
}

/**
 * 撤回渠道优秀代表申请
 * @param {Number} id - 申请ID
 * @returns {Promise}
 */
export function withdrawExcellentRepresentativeApplication(id) {
  return request({
    url: '/certification/excellent-representative/withdraw/' + id,
    method: 'delete'
  })
}